/**
 * Shared range/toggle filter components for every 035 report (task 035-01,
 * extracted from `co-op-counts.tsx` per README decision 10 — same markup,
 * classes and test-id shape, so the co-op screen's re-wire changes no
 * rendered string, test id or role).
 *
 * Task 087-01: the date selection, campaign status and "Show test leads" live
 * in the URL query (`range`, `from`, `to`, `status`, `test`) so they survive a
 * tab switch, a reload and a shared link; the Today preset; and the per-tab
 * search pieces.
 */

import type { ReactNode } from 'react';
import { useSearchParams, type SetURLSearchParams } from 'react-router-dom';
import type { CampaignStatus } from '@leads-platform/shared';
import { campaignStatusEnum } from '@leads-platform/shared';
import { DateRangeField } from '../../components/date-range-field';
import { SearchInput } from '../../components/ui/search-input';
import { Toggle } from '../../components/ui/toggle';
import { lastMonthRange, thisMonthRange, todayRange, type Preset } from './date-presets';

const PRESETS: readonly [Preset, string][] = [
  ['today', 'Today'],
  ['this_month', 'This month'],
  ['last_month', 'Last month'],
  ['custom', 'Custom'],
];

interface PresetStripProps {
  preset: Preset;
  onChange: (p: Preset) => void;
  /** `data-testid={`${testIdPrefix}-preset-${p}`}` — `coop`, `custom-optin`, `cto`, `paid-social`. */
  testIdPrefix: string;
}

export function PresetStrip({ preset, onChange, testIdPrefix }: PresetStripProps) {
  return (
    <div className="flex gap-1">
      {PRESETS.map(([p, label]) => (
        <button
          key={p}
          type="button"
          data-testid={`${testIdPrefix}-preset-${p}`}
          onClick={() => onChange(p)}
          className={`rounded-md px-2 py-0.5 text-xs font-medium transition ${
            preset === p ? 'bg-accent text-white' : 'bg-paper text-faint hover:text-ink hairline'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

interface RangeFiltersProps {
  preset: Preset;
  from: string;
  to: string;
  applyPreset: (p: Preset) => void;
  setRange: (from: string, to: string) => void;
  testIdPrefix: string;
}

/** Preset strip + the custom-range field, shown only when `preset === 'custom'`. */
export function RangeFilters({
  preset,
  from,
  to,
  applyPreset,
  setRange,
  testIdPrefix,
}: RangeFiltersProps) {
  return (
    <>
      <PresetStrip preset={preset} onChange={applyPreset} testIdPrefix={testIdPrefix} />
      {preset === 'custom' && (
        <div className="w-64">
          <DateRangeField from={from} to={to} onChange={setRange} />
        </div>
      )}
    </>
  );
}

interface TestLeadsToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

/** The one `role="switch"` on a report screen — `Toggle` forwards no DOM props. */
export function TestLeadsToggle({ checked, onChange }: TestLeadsToggleProps) {
  return <Toggle checked={checked} onChange={onChange} label="Show test leads" />;
}

/** The query keys a report selection lives in, in the order the tab links carry them. */
export const SHARED_REPORT_KEYS = ['range', 'from', 'to', 'status', 'test'] as const;

const RANGE_KEY = 'range';
const FROM_KEY = 'from';
const TO_KEY = 'to';
const STATUS_KEY = 'status';
const TEST_KEY = 'test';

/** `range` values that are stored; `this_month` is the default and is never written. */
const PRESET_BY_PARAM: ReadonlyMap<string, Preset> = new Map<string, Preset>([
  ['today', 'today'],
  ['last_month', 'last_month'],
  ['custom', 'custom'],
]);

const STATUS_BY_PARAM: ReadonlyMap<string, CampaignStatus> = new Map(
  campaignStatusEnum.options.map((status) => [status, status])
);

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

function isYmd(value: string): boolean {
  const m = YMD.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
}

/** A custom-range side: a valid `YYYY-MM-DD`, else `''` (open). */
function readYmd(params: URLSearchParams, key: string): string {
  const value = params.get(key) ?? '';
  return isYmd(value) ? value : '';
}

/** A complete custom `from > to` can only be a hand-typed or stale URL — swap it. */
function readCustomRange(params: URLSearchParams): [string, string] {
  const from = readYmd(params, FROM_KEY);
  const to = readYmd(params, TO_KEY);
  return from && to && from > to ? [to, from] : [from, to];
}

function rangeFor(preset: Preset, params: URLSearchParams): [string, string] {
  switch (preset) {
    case 'today':
      return todayRange();
    case 'last_month':
      return lastMonthRange();
    case 'custom':
      return readCustomRange(params);
    case 'this_month':
      return thisMonthRange();
  }
}

function setOrDelete(params: URLSearchParams, key: string, value: string) {
  if (value) params.set(key, value);
  else params.delete(key);
}

/**
 * One functional write per user action, replacing history and preserving every
 * key the edit doesn't touch.
 */
function writeParams(setSearchParams: SetURLSearchParams, edit: (next: URLSearchParams) => void) {
  setSearchParams(
    (prev) => {
      const next = new URLSearchParams(prev);
      edit(next);
      return next;
    },
    { replace: true }
  );
}

export interface ReportRange {
  preset: Preset;
  from: string;
  to: string;
  applyPreset: (p: Preset) => void;
  setRange: (from: string, to: string) => void;
}

/**
 * Preset + custom-range state for a report screen, backed by the URL query
 * (task 087-01 D2). A preset is stored by name and recomputed at render, so a
 * reload or a link opened tomorrow shows that day; `custom` reads `from` / `to`
 * (each side may be empty mid-pick). `setRange` is the raw setter
 * `DateRangeField`'s `onChange` calls directly on `custom`.
 */
export function useReportRange(): ReportRange {
  const [searchParams, setSearchParams] = useSearchParams();
  const preset = PRESET_BY_PARAM.get(searchParams.get(RANGE_KEY) ?? '') ?? 'this_month';

  const [from, to] = rangeFor(preset, searchParams);

  const applyPreset = (p: Preset) => {
    writeParams(setSearchParams, (next) => {
      if (p === 'this_month') next.delete(RANGE_KEY);
      else next.set(RANGE_KEY, p);
      if (p === 'custom') {
        setOrDelete(next, FROM_KEY, from);
        setOrDelete(next, TO_KEY, to);
      } else {
        next.delete(FROM_KEY);
        next.delete(TO_KEY);
      }
    });
  };

  const setRange = (f: string, t: string) => {
    writeParams(setSearchParams, (next) => {
      next.set(RANGE_KEY, 'custom');
      setOrDelete(next, FROM_KEY, f);
      setOrDelete(next, TO_KEY, t);
    });
  };

  return { preset, from, to, applyPreset, setRange };
}

export interface SharedReportFilters {
  campaignStatus: CampaignStatus | '';
  setCampaignStatus: (status: CampaignStatus | '') => void;
  includeTest: boolean;
  setIncludeTest: (includeTest: boolean) => void;
}

/**
 * The campaign status and "Show test leads" selection every style tab shares,
 * URL-backed (`status`, `test=1`; defaults are never written). An unknown
 * `status` reads as "all".
 */
export function useSharedReportFilters(): SharedReportFilters {
  const [searchParams, setSearchParams] = useSearchParams();
  const campaignStatus = STATUS_BY_PARAM.get(searchParams.get(STATUS_KEY) ?? '') ?? '';
  const includeTest = searchParams.get(TEST_KEY) === '1';

  const setCampaignStatus = (status: CampaignStatus | '') => {
    writeParams(setSearchParams, (next) => setOrDelete(next, STATUS_KEY, status));
  };

  const setIncludeTest = (checked: boolean) => {
    writeParams(setSearchParams, (next) => setOrDelete(next, TEST_KEY, checked ? '1' : ''));
  };

  return { campaignStatus, setCampaignStatus, includeTest, setIncludeTest };
}

/** `''` or `'?…'` holding only the shared selection keys of `search`, in `SHARED_REPORT_KEYS` order. */
export function carrySharedReportParams(search: string): string {
  const source = new URLSearchParams(search);
  const carried = new URLSearchParams();
  for (const key of SHARED_REPORT_KEYS) {
    const value = source.get(key);
    if (value !== null) carried.set(key, value);
  }
  const query = carried.toString();
  return query ? `?${query}` : '';
}

/** Case-insensitive substring match of `q.trim()` over `fields`; a blank `q` matches everything. */
export function matchesSearch(q: string, ...fields: (string | null | undefined)[]): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return fields.some((field) => (field ?? '').toLowerCase().includes(needle));
}

interface ReportSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  testId: string;
}

/** The per-tab search box (`SearchInput` takes no test-id prop, so it sits in a tagged wrapper). */
export function ReportSearch({ value, onChange, placeholder, testId }: ReportSearchProps) {
  return (
    <div data-testid={testId}>
      <SearchInput value={value} onChange={onChange} placeholder={placeholder} />
    </div>
  );
}

interface SearchNoteProps {
  /** A search term is entered. */
  active: boolean;
  shown: number;
  total: number;
  testId: string;
}

/** Totals are server-computed and non-additive, so a search says they still cover every row. */
export function SearchNote({ active, shown, total, testId }: SearchNoteProps): ReactNode {
  if (!active || shown === total) return null;
  return (
    <p
      data-testid={testId}
      className="border-b border-line px-4 py-2 text-xs text-muted-foreground"
    >
      Showing {shown} of {total} rows · totals cover every row
    </p>
  );
}
